const GL_PARAMS_CONVERSION_LINKER = '_gl';

/**
 * getDecoratedUrlWithConversionLinker
 *
 * This function ensures that a given URL is properly "decorated" with Google Ads conversion tracking parameters,
 * by either extracting them from the current page or triggering the Google Tag Manager (GTM) Conversion Linker mechanism.
 *
 * 🧠 Why this is necessary:
 * Google Tag Manager's Conversion Linker is responsible for appending URL parameters like `_gl`, `gclid`, or `_gcl_aw`
 * to outbound links in order to maintain user identity and conversion tracking across domains (cross-domain tracking).
 *
 * The Conversion Linker typically works by detecting user-initiated interactions with <a> elements (e.g. `mousedown`, `click`)
 * and dynamically modifying the `href` before the browser navigates away. However, in modern JavaScript apps like SPAs,
 * redirections are often handled programmatically (e.g. `window.location.href = ...`) without real DOM links or user events.
 * This prevents GTM from intercepting and decorating the URL automatically.
 *
 * ✅ What this function does:
 * 1. It first checks if the `_gl` parameter is already present in the current URL (hash or query string).
 *    - If found, it appends this value directly to the given target URL.
 * 2. If not found, it:
 *    - Programmatically creates an invisible `<a>` tag pointing to the target URL.
 *    - Triggers a `mousedown` event on it, mimicking a user interaction.
 *    - Allows GTM's Conversion Linker to intercept and decorate the link with tracking parameters.
 *    - Extracts the decorated `href` and returns it.
 *
 * 🚀 Use this function when:
 * - You need to programmatically redirect the user to another domain (e.g., `window.location.href`),
 *   and want to ensure proper tracking continuity across that navigation.
 * - You want to preserve Google Ads conversion attribution data using the GTM Conversion Linker.
 *
 * @param url - The original target URL (e.g., https://your-landing-page.com)
 * @returns string - The decorated URL containing conversion tracking parameters, if applicable
 */
export function getDecoratedUrlWithConversionLinker(url: string): string {
    const glParam = extractConversionLinkerParam();

    if (glParam) {
        const urlWithParam = new URL(url);
        urlWithParam.searchParams.set(GL_PARAMS_CONVERSION_LINKER, glParam);
        return urlWithParam.toString();
    }

    return decorateUrlWithConversionLinker(url);
}

/**
 * extractConversionLinkerParam
 *
 * Extracts the `_gl` conversion linker parameter from the current page URL, looking in both the hash and query string.
 *
 * @returns string | null - The `_gl` value if found, otherwise null
 */
function extractConversionLinkerParam(): string | null {
    const hashOrSearch = window.location.hash || window.location.search;
    const queryString = hashOrSearch.includes('?') ? hashOrSearch.split('?')[1] : '';
    return new URLSearchParams(queryString).get(GL_PARAMS_CONVERSION_LINKER);
}

/**
 * decorateUrlWithConversionLinker
 *
 * Uses GTM's Conversion Linker by simulating a user interaction with an <a> tag to decorate the URL.
 * This ensures the URL includes tracking parameters like `_gl`, `gclid`, etc., which GTM appends on real link interactions.
 *
 * @param url - The original, undecorated target URL
 * @returns string - The URL as decorated by GTM's Conversion Linker
 */
function decorateUrlWithConversionLinker(url: string): string {
    const link = document.createElement('a');
    link.style.display = 'none';
    link.href = url;
    link.id = 'redirectLink';

    document.body.appendChild(link);

    link.dispatchEvent(new MouseEvent('mousedown', {
        bubbles: true,
        cancelable: true,
        view: window
    }));

    const decoratedUrl = link.href;

    document.body.removeChild(link);

    return decoratedUrl;
}
